/**
 * Commands: the sole input to the simulation (R4).
 *
 * There is no other mutation path. Input, the skirmish AI, and a remote peer
 * all reach the simulation through this file and nothing else.
 *
 * This file sits **inside the simulation-purity lint fence** (KTD1). That is
 * not decoration: an accept/reject decision that differs between two clients
 * desyncs exactly like a divergent simulation step, and the lint rule is the
 * only thing that would catch a wall-clock read or an approximated function
 * creeping into validation.
 */

import type { Fixed } from '../core/fixed.js'
import { MAP_SIZE, MAX_COMMAND_UNITS, MAX_INPUT_DELAY_TICKS } from '../core/config.js'

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

export const CMD = {
  Move: 1,
  AttackMove: 2,
  Attack: 3,
  Stop: 4,
  Guard: 5,
  Scatter: 6,
  Queue: 7,
  Cancel: 8,
  Place: 9,
  Deploy: 10,
  Mine: 11,
  Unload: 12,
  SuperFire: 13,
  Repair: 14,
  Sell: 15,
  Rally: 16,
  Waypoint: 17,
  Primary: 18,
  Load: 19,
  Mend: 20,
} as const

export type CmdType = (typeof CMD)[keyof typeof CMD]

/** Fields every command carries. `seq` orders same-slot commands within a tick. */
interface Base {
  slot: number
  seq: number
}

export interface MoveCmd extends Base {
  t: typeof CMD.Move | typeof CMD.AttackMove
  units: Uint32Array
  x: Fixed
  y: Fixed
}
/**
 * A move appended to whatever a unit is already doing, rather than replacing it.
 *
 * Separate from `Move` so that the ordinary case stays destructive: a plain
 * right-click must cancel the previous order, and a flag on `Move` would make
 * every caller responsible for clearing it.
 */
export interface WaypointCmd extends Base {
  t: typeof CMD.Waypoint
  units: Uint32Array
  x: Fixed
  y: Fixed
}
export interface AttackCmd extends Base {
  t: typeof CMD.Attack
  units: Uint32Array
  target: number
}
export interface UnitStateCmd extends Base {
  t: typeof CMD.Stop | typeof CMD.Guard | typeof CMD.Scatter
  units: Uint32Array
}
export interface TargetedUnitCmd extends Base {
  t: typeof CMD.Mine | typeof CMD.Unload
  units: Uint32Array
  /** Cell for Mine, entity id for Unload. */
  target: number
}
export interface QueueCmd extends Base {
  t: typeof CMD.Queue
  tab: number
  kind: number
}
export interface CancelCmd extends Base {
  t: typeof CMD.Cancel
  tab: number
  /** Queue index to cancel; 0 is the item currently building. */
  index: number
}
export interface PlaceCmd extends Base {
  t: typeof CMD.Place
  kind: number
  cx: number
  cy: number
}
export interface DeployCmd extends Base {
  t: typeof CMD.Deploy
  unit: number
}
/**
 * Both act on one finished structure the sender owns.
 *
 * Repair is a toggle rather than a one-shot: it runs until the structure is
 * whole, the owner turns it off, or the money runs out, which is what makes it
 * something you leave on during a fight instead of spamming.
 */
export interface StructureCmd extends Base {
  t: typeof CMD.Repair | typeof CMD.Sell | typeof CMD.Primary
  structure: number
}
/**
 * Where a production structure sends what it builds.
 *
 * Carries the structure so the rejection can name it, and a cell rather than a
 * fixed-point position: a rally point is a place on the map, not a precise
 * coordinate, and rounding it here keeps two clients from disagreeing about
 * which cell a click landed in.
 */
/** Put an infantryman inside a transport. */
export interface LoadCmd extends Base {
  t: typeof CMD.Load
  unit: number
  transport: number
}
/** An engineer enters an adjacent friendly structure and repairs it to full. */
export interface MendCmd extends Base {
  t: typeof CMD.Mend
  unit: number
  structure: number
}
export interface RallyCmd extends Base {
  t: typeof CMD.Rally
  structure: number
  cx: number
  cy: number
}
export interface SuperCmd extends Base {
  t: typeof CMD.SuperFire
  kind: number
  cx: number
  cy: number
}

export type Command =
  | MoveCmd
  | WaypointCmd
  | AttackCmd
  | UnitStateCmd
  | TargetedUnitCmd
  | QueueCmd
  | CancelCmd
  | PlaceCmd
  | DeployCmd
  | StructureCmd
  | LoadCmd
  | MendCmd
  | RallyCmd
  | SuperCmd

/** The closed set of reasons an action can be refused (R33). */
export const REASON = {
  NotOwned: 'not-owned',
  OutOfBounds: 'out-of-bounds',
  StaleEntity: 'stale-entity',
  TooManyUnits: 'too-many-units',
  WrongSlot: 'wrong-slot',
  BadTick: 'bad-tick',
  Malformed: 'malformed',
  Occupied: 'occupied',
  Cliff: 'cliff',
  Water: 'water',
  Ramp: 'ramp',
  OutsideBuildArea: 'outside-build-area',
  QueueFull: 'queue-full',
  InsufficientCredits: 'insufficient-credits',
  Unreachable: 'unreachable',
  NotCharged: 'not-charged',
  NoProducer: 'no-producer',
  NotStructure: 'not-structure',
  Undamaged: 'undamaged',
  StillBuilding: 'still-building',
} as const

export type Reason = (typeof REASON)[keyof typeof REASON]

/**
 * Player-facing wording for a refusal. Lives beside the reasons themselves so
 * that adding one without wording is a type error rather than a raw
 * `outside-build-area` shown to the player.
 */
export const REASON_TEXT: Record<Reason, string> = {
  [REASON.NotOwned]: 'Not your unit',
  [REASON.OutOfBounds]: 'Off the map',
  [REASON.StaleEntity]: 'Target no longer exists',
  [REASON.TooManyUnits]: 'Too many units in one order',
  [REASON.WrongSlot]: 'Not your turn to act',
  [REASON.BadTick]: 'Command arrived out of time',
  [REASON.Malformed]: 'Malformed order',
  [REASON.Occupied]: 'Cannot build there: cell occupied',
  [REASON.Cliff]: 'Cannot build there: cliff',
  [REASON.Water]: 'Cannot build there: ice',
  [REASON.Ramp]: 'Cannot build on a ramp',
  [REASON.OutsideBuildArea]: 'Too far from your base',
  [REASON.QueueFull]: 'Queue full',
  [REASON.InsufficientCredits]: 'Insufficient funds',
  [REASON.Unreachable]: 'Unreachable destination',
  [REASON.NotCharged]: 'Superweapon not charged',
  [REASON.NoProducer]: 'No production structure',
  [REASON.NotStructure]: 'Only structures can be repaired or sold',
  [REASON.Undamaged]: 'Already at full strength',
  [REASON.StillBuilding]: 'Still under construction',
}

// ---------------------------------------------------------------------------
// Validation (R35)
// ---------------------------------------------------------------------------

/**
 * What validation needs to know about the world. The simulation supplies it;
 * keeping it an interface is what lets commands.ts stay free of sim imports.
 */
export interface CommandWorld {
  /** True if the id resolves to a live entity — generation-checked. */
  isLive(id: number): boolean
  /** Owning player slot of a live entity, or -1. */
  ownerOf(id: number): number
  /** True if the entity is a structure (structures ignore movement orders). */
  isStructure?(id: number): boolean
}

export interface ValidationContext {
  world: CommandWorld
  /** The tick currently being executed, or about to be. */
  currentTick: number
  /** Tick this command is scheduled to execute at. */
  execTick: number
  /** The slot the message actually arrived from. */
  senderSlot: number
  maxDelay?: number
}

export interface Rejection {
  reason: Reason
  detail?: string
}

/**
 * Validate one command. Returns `null` when it is acceptable.
 *
 * A command failing any check is dropped **whole**, never partially applied —
 * a half-applied order is a divergence between the client that dropped it and
 * the one that did not.
 */
export function validate(cmd: Command, ctx: ValidationContext): Rejection | null {
  if (!isWellFormed(cmd)) return { reason: REASON.Malformed }

  // The slot must match the sender. Otherwise a peer issues orders as you.
  if (cmd.slot !== ctx.senderSlot) return { reason: REASON.WrongSlot }

  // The execution tick must fall inside the accepted delay window. Without
  // this a hostile client submits for an already-committed tick, or for one
  // millions ahead, and the ring either indexes outside itself or overwrites
  // committed state.
  const maxDelay = ctx.maxDelay ?? MAX_INPUT_DELAY_TICKS
  if (ctx.execTick <= ctx.currentTick) return { reason: REASON.BadTick, detail: 'already committed' }
  if (ctx.execTick > ctx.currentTick + maxDelay) {
    return { reason: REASON.BadTick, detail: 'beyond delay window' }
  }

  if ('units' in cmd) {
    if (cmd.units.length === 0) return { reason: REASON.Malformed, detail: 'empty unit list' }
    if (cmd.units.length > MAX_COMMAND_UNITS) return { reason: REASON.TooManyUnits }
    for (let i = 0; i < cmd.units.length; i++) {
      const id = cmd.units[i]
      if (!ctx.world.isLive(id)) return { reason: REASON.StaleEntity }
      // Ownership is otherwise enforced only in the selection UI, which is
      // exactly the client-side rule a modified client removes.
      if (ctx.world.ownerOf(id) !== cmd.slot) return { reason: REASON.NotOwned }
    }
  }

  switch (cmd.t) {
    case CMD.Move:
    case CMD.AttackMove:
    case CMD.Waypoint:
      if (!inWorldBounds(cmd.x, cmd.y)) return { reason: REASON.OutOfBounds }
      return null

    case CMD.Attack:
    case CMD.Unload:
      if (!ctx.world.isLive(cmd.target)) return { reason: REASON.StaleEntity, detail: 'target' }
      return null

    case CMD.Mine:
      if (!inCellBounds(cmd.target % MAP_SIZE, (cmd.target / MAP_SIZE) | 0)) {
        return { reason: REASON.OutOfBounds }
      }
      if (cmd.target < 0 || cmd.target >= MAP_SIZE * MAP_SIZE) return { reason: REASON.OutOfBounds }
      return null

    case CMD.Place:
    case CMD.SuperFire:
      if (!inCellBounds(cmd.cx, cmd.cy)) return { reason: REASON.OutOfBounds }
      return null

    case CMD.Deploy:
      if (!ctx.world.isLive(cmd.unit)) return { reason: REASON.StaleEntity }
      if (ctx.world.ownerOf(cmd.unit) !== cmd.slot) return { reason: REASON.NotOwned }
      return null

    case CMD.Load:
      if (!ctx.world.isLive(cmd.unit)) return { reason: REASON.StaleEntity }
      if (!ctx.world.isLive(cmd.transport)) return { reason: REASON.StaleEntity }
      if (ctx.world.ownerOf(cmd.unit) !== cmd.slot) return { reason: REASON.NotOwned }
      if (ctx.world.ownerOf(cmd.transport) !== cmd.slot) return { reason: REASON.NotOwned }
      return null

    case CMD.Mend:
      if (!ctx.world.isLive(cmd.unit)) return { reason: REASON.StaleEntity }
      if (!ctx.world.isLive(cmd.structure)) return { reason: REASON.StaleEntity }
      if (ctx.world.ownerOf(cmd.unit) !== cmd.slot) return { reason: REASON.NotOwned }
      if (ctx.world.ownerOf(cmd.structure) !== cmd.slot) return { reason: REASON.NotOwned }
      return null

    case CMD.Rally:
      if (!inCellBounds(cmd.cx, cmd.cy)) return { reason: REASON.OutOfBounds }
      if (!ctx.world.isLive(cmd.structure)) return { reason: REASON.StaleEntity }
      if (ctx.world.ownerOf(cmd.structure) !== cmd.slot) return { reason: REASON.NotOwned }
      return null

    case CMD.Repair:
    case CMD.Sell:
    case CMD.Primary:
      if (!ctx.world.isLive(cmd.structure)) return { reason: REASON.StaleEntity }
      if (ctx.world.ownerOf(cmd.structure) !== cmd.slot) return { reason: REASON.NotOwned }
      // `isStructure` is optional on the interface, so a host that does not
      // supply it leaves this to the simulation rather than passing a unit
      // through as a building.
      if (ctx.world.isStructure && !ctx.world.isStructure(cmd.structure)) {
        return { reason: REASON.NotStructure }
      }
      return null

    case CMD.Queue:
    case CMD.Cancel:
    case CMD.Stop:
    case CMD.Guard:
    case CMD.Scatter:
      return null

    default:
      return { reason: REASON.Malformed }
  }
}

// Derived rather than naming the last member: Primary and Load were appended
// to CMD after a literal `cmd.t > CMD.Waypoint` bound was written here, and
// every such command was silently rejected as malformed.
const CMD_MAX = Math.max(...Object.values(CMD))

function isWellFormed(cmd: Command): boolean {
  if (!cmd || typeof cmd !== 'object') return false
  if (!Number.isInteger(cmd.t) || cmd.t < CMD.Move || cmd.t > CMD_MAX) return false
  if (!Number.isInteger(cmd.slot) || cmd.slot < 0 || cmd.slot > 15) return false
  if (!Number.isInteger(cmd.seq) || cmd.seq < 0) return false
  return true
}

function inWorldBounds(x: Fixed, y: Fixed): boolean {
  const limit = MAP_SIZE << 16
  return Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < limit && y < limit
}

function inCellBounds(cx: number, cy: number): boolean {
  return (
    Number.isInteger(cx) && Number.isInteger(cy) && cx >= 0 && cy >= 0 && cx < MAP_SIZE && cy < MAP_SIZE
  )
}

// ---------------------------------------------------------------------------
// Ordering
// ---------------------------------------------------------------------------

/**
 * Total order over a tick's commands: `(playerSlot, seq)`.
 *
 * Never arrival time. Ordering by arrival is the obvious mistake and is
 * nondeterministic by construction — the two clients see different orders and
 * diverge on the first tick where two players act simultaneously.
 */
export function compareCommands(a: Command, b: Command): number {
  if (a.slot !== b.slot) return a.slot - b.slot
  if (a.seq !== b.seq) return a.seq - b.seq
  return a.t - b.t
}

export function sortCommands(list: Command[]): Command[] {
  list.sort(compareCommands)
  return list
}

// ---------------------------------------------------------------------------
// Binary serialization
// ---------------------------------------------------------------------------

const HEADER_BYTES = 8 // type u8, slot u8, pad u16, seq u32

/** Byte length of one encoded command. */
export function byteLength(cmd: Command): number {
  switch (cmd.t) {
    case CMD.Move:
    case CMD.AttackMove:
    case CMD.Waypoint:
      return HEADER_BYTES + 4 + 4 + 2 + cmd.units.length * 4
    case CMD.Attack:
      return HEADER_BYTES + 4 + 2 + cmd.units.length * 4
    case CMD.Mine:
    case CMD.Unload:
      return HEADER_BYTES + 4 + 2 + cmd.units.length * 4
    case CMD.Stop:
    case CMD.Guard:
    case CMD.Scatter:
      return HEADER_BYTES + 2 + cmd.units.length * 4
    case CMD.Queue:
    case CMD.Cancel:
      return HEADER_BYTES + 4
    case CMD.Place:
    case CMD.SuperFire:
      return HEADER_BYTES + 6
    case CMD.Deploy:
    case CMD.Repair:
    case CMD.Sell:
    case CMD.Primary:
      return HEADER_BYTES + 4
    case CMD.Load:
    case CMD.Mend:
      return HEADER_BYTES + 8
    case CMD.Rally:
      return HEADER_BYTES + 8
    default:
      return HEADER_BYTES
  }
}

/** Encode into `view` at `offset`; returns the new offset. Little-endian. */
export function encode(cmd: Command, view: DataView, offset: number): number {
  let o = offset
  view.setUint8(o, cmd.t)
  view.setUint8(o + 1, cmd.slot)
  view.setUint16(o + 2, 0, true)
  view.setUint32(o + 4, cmd.seq, true)
  o += HEADER_BYTES

  switch (cmd.t) {
    case CMD.Move:
    case CMD.AttackMove:
    case CMD.Waypoint:
      view.setInt32(o, cmd.x, true)
      view.setInt32(o + 4, cmd.y, true)
      o += 8
      return writeUnits(cmd.units, view, o)

    case CMD.Attack:
      view.setUint32(o, cmd.target, true)
      return writeUnits(cmd.units, view, o + 4)

    case CMD.Mine:
    case CMD.Unload:
      view.setUint32(o, cmd.target, true)
      return writeUnits(cmd.units, view, o + 4)

    case CMD.Stop:
    case CMD.Guard:
    case CMD.Scatter:
      return writeUnits(cmd.units, view, o)

    case CMD.Queue:
      view.setUint16(o, cmd.tab, true)
      view.setUint16(o + 2, cmd.kind, true)
      return o + 4

    case CMD.Cancel:
      view.setUint16(o, cmd.tab, true)
      view.setUint16(o + 2, cmd.index, true)
      return o + 4

    case CMD.Place:
    case CMD.SuperFire:
      view.setUint16(o, cmd.kind, true)
      view.setUint16(o + 2, cmd.cx, true)
      view.setUint16(o + 4, cmd.cy, true)
      return o + 6

    case CMD.Deploy:
      view.setUint32(o, cmd.unit, true)
      return o + 4

    case CMD.Repair:
    case CMD.Sell:
    case CMD.Primary:
      view.setUint32(o, cmd.structure, true)
      return o + 4

    case CMD.Load:
      view.setUint32(o, cmd.unit, true)
      view.setUint32(o + 4, cmd.transport, true)
      return o + 8

    case CMD.Mend:
      view.setUint32(o, cmd.unit, true)
      view.setUint32(o + 4, cmd.structure, true)
      return o + 8

    case CMD.Rally:
      view.setUint32(o, cmd.structure, true)
      view.setUint16(o + 4, cmd.cx, true)
      view.setUint16(o + 6, cmd.cy, true)
      return o + 8

    default:
      return o
  }
}

function writeUnits(units: Uint32Array, view: DataView, offset: number): number {
  view.setUint16(offset, units.length, true)
  let o = offset + 2
  for (let i = 0; i < units.length; i++, o += 4) view.setUint32(o, units[i], true)
  return o
}

export interface DecodeResult {
  cmd: Command
  offset: number
}

/**
 * Decode one command. Returns `null` on a malformed buffer rather than a
 * partially-decoded command — a half-decoded order is worse than none.
 */
export function decode(view: DataView, offset: number): DecodeResult | null {
  if (offset + HEADER_BYTES > view.byteLength) return null
  const t = view.getUint8(offset) as CmdType
  const slot = view.getUint8(offset + 1)
  const seq = view.getUint32(offset + 4, true)
  let o = offset + HEADER_BYTES

  const readUnits = (): Uint32Array | null => {
    if (o + 2 > view.byteLength) return null
    const n = view.getUint16(o, true)
    o += 2
    if (n > MAX_COMMAND_UNITS) return null
    if (o + n * 4 > view.byteLength) return null
    const units = new Uint32Array(n)
    for (let i = 0; i < n; i++, o += 4) units[i] = view.getUint32(o, true)
    return units
  }

  switch (t) {
    case CMD.Move:
    case CMD.AttackMove:
    case CMD.Waypoint: {
      if (o + 8 > view.byteLength) return null
      const x = view.getInt32(o, true) as Fixed
      const y = view.getInt32(o + 4, true) as Fixed
      o += 8
      const units = readUnits()
      if (!units) return null
      return { cmd: { t, slot, seq, units, x, y }, offset: o }
    }
    case CMD.Attack: {
      if (o + 4 > view.byteLength) return null
      const target = view.getUint32(o, true)
      o += 4
      const units = readUnits()
      if (!units) return null
      return { cmd: { t, slot, seq, units, target }, offset: o }
    }
    case CMD.Mine:
    case CMD.Unload: {
      if (o + 4 > view.byteLength) return null
      const target = view.getUint32(o, true)
      o += 4
      const units = readUnits()
      if (!units) return null
      return { cmd: { t, slot, seq, units, target }, offset: o }
    }
    case CMD.Stop:
    case CMD.Guard:
    case CMD.Scatter: {
      const units = readUnits()
      if (!units) return null
      return { cmd: { t, slot, seq, units }, offset: o }
    }
    case CMD.Queue: {
      if (o + 4 > view.byteLength) return null
      return {
        cmd: { t, slot, seq, tab: view.getUint16(o, true), kind: view.getUint16(o + 2, true) },
        offset: o + 4,
      }
    }
    case CMD.Cancel: {
      if (o + 4 > view.byteLength) return null
      return {
        cmd: { t, slot, seq, tab: view.getUint16(o, true), index: view.getUint16(o + 2, true) },
        offset: o + 4,
      }
    }
    case CMD.Place:
    case CMD.SuperFire: {
      if (o + 6 > view.byteLength) return null
      return {
        cmd: {
          t,
          slot,
          seq,
          kind: view.getUint16(o, true),
          cx: view.getUint16(o + 2, true),
          cy: view.getUint16(o + 4, true),
        },
        offset: o + 6,
      }
    }
    case CMD.Deploy: {
      if (o + 4 > view.byteLength) return null
      return { cmd: { t, slot, seq, unit: view.getUint32(o, true) }, offset: o + 4 }
    }
    case CMD.Repair:
    case CMD.Sell:
    case CMD.Primary: {
      if (o + 4 > view.byteLength) return null
      return { cmd: { t, slot, seq, structure: view.getUint32(o, true) }, offset: o + 4 }
    }
    case CMD.Load: {
      if (o + 8 > view.byteLength) return null
      return {
        cmd: { t, slot, seq, unit: view.getUint32(o, true), transport: view.getUint32(o + 4, true) },
        offset: o + 8,
      }
    }
    case CMD.Mend: {
      if (o + 8 > view.byteLength) return null
      return {
        cmd: { t, slot, seq, unit: view.getUint32(o, true), structure: view.getUint32(o + 4, true) },
        offset: o + 8,
      }
    }
    case CMD.Rally: {
      if (o + 8 > view.byteLength) return null
      return {
        cmd: {
          t,
          slot,
          seq,
          structure: view.getUint32(o, true),
          cx: view.getUint16(o + 4, true),
          cy: view.getUint16(o + 6, true),
        },
        offset: o + 8,
      }
    }
    default:
      return null
  }
}

/** Encode a whole tick's set into one buffer. */
export function encodeSet(commands: readonly Command[]): Uint8Array {
  let total = 0
  for (const c of commands) total += byteLength(c)
  const buf = new Uint8Array(total)
  const view = new DataView(buf.buffer)
  let o = 0
  for (const c of commands) o = encode(c, view, o)
  return buf
}

/** Decode a whole set. Returns `null` if any command is malformed. */
export function decodeSet(bytes: Uint8Array): Command[] | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const out: Command[] = []
  let o = 0
  while (o < bytes.byteLength) {
    const r = decode(view, o)
    if (!r || r.offset <= o) return null
    out.push(r.cmd)
    o = r.offset
  }
  return out
}
